import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import {
  isPriorConsentRegion,
  hasAnsweredBanner,
  savePrefs,
} from '../../utils/privacyConsent';

// Consent banner for the EEA, the UK and Switzerland.
//
// In those regions nonessential tags default to denied (see the Consent Mode
// block in public/index.html) and may only start collecting once the visitor
// makes an affirmative choice here. Everywhere else the banner does not appear:
// those visitors are on the opt-out model and use "Do Not Sell or Share" in
// Settings instead.
//
// Dismissing the banner without choosing is not consent — nothing is granted and
// the banner returns on the next load.
const CookieBanner = () => {
  const navigate = useNavigate();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    // Only where prior consent is required, and only until it has been given.
    if (isPriorConsentRegion() && !hasAnsweredBanner()) {
      setShowBanner(true);
    }
  }, []);

  // One place to record the choice: it goes to the shared store (so qushi.io and
  // this app agree), reaches every tag through Consent Mode, and starts any tag
  // that was waiting on consent. Declining advertising is also recorded as a
  // sale/share opt-out so the two controls can never contradict each other.
  const persist = (analyticsConsent, adsConsent) => {
    savePrefs({
      analyticsConsent,
      adsConsent,
      doNotSellOrShare: !adsConsent,
    });

    // Legacy keys, kept so nothing that still reads them changes behaviour.
    try {
      localStorage.setItem('cookiePreference', analyticsConsent && adsConsent ? 'all' : 'custom');
      localStorage.setItem('cookieConsent', JSON.stringify({
        essential: true,
        analytics: analyticsConsent,
        marketing: adsConsent,
        timestamp: Date.now(),
      }));
    } catch (e) { }

    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptAll = () => persist(true, true);
  const handleRejectAll = () => persist(false, false);
  const handleSaveSettings = () => persist(analytics, marketing);

  const handleSettings = () => {
    setShowSettings(true);
  };

  const handleClose = () => {
    // Dismiss only. Nothing is stored, so consent stays denied.
    setShowBanner(false);
  };

  const openCookiePolicy = () => {
    // Navigate within SPA to avoid popup blockers and ensure routing
    navigate('/cookie-policy');
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-4 md:right-auto md:w-96 z-50">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4">
        <div className="flex justify-between items-start mb-3">
          <h3 className="text-sm font-semibold text-gray-800">Cookie Consent</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} size="sm" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-xs text-gray-600 leading-relaxed">
            We use essential cookies to run QUSHi. With your permission we would
            also use analytics cookies to understand how the app is used, and
            advertising cookies to measure our campaigns. For more details, read our{' '}
            <button
              onClick={openCookiePolicy}
              className="text-blue-600 hover:text-blue-700 underline font-medium"
            >
              Cookie Policy
            </button>.
          </p>
        </div>

        {!showSettings ? (
          <div className="space-y-2">
            <button
              onClick={handleAcceptAll}
              className="w-full bg-gray-800 hover:bg-gray-900 text-white text-xs font-medium py-2 px-4 rounded-md transition-colors"
            >
              Accept All Cookies
            </button>
            <button
              onClick={handleRejectAll}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs font-medium py-2 px-4 rounded-md transition-colors"
            >
              Reject All
            </button>
            <button
              onClick={handleSettings}
              className="w-full text-gray-600 hover:text-gray-800 text-xs font-medium py-1 transition-colors"
            >
              Cookie Settings
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-700">Essential Cookies</label>
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  className="rounded border-gray-300"
                />
              </div>
              <p className="text-xs text-gray-500">
                Required for basic site functionality.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-700">Analytics Cookies</label>
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="rounded border-gray-300"
                />
              </div>
              <p className="text-xs text-gray-500">
                Help us understand how you use our site.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-700">Marketing Cookies</label>
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="rounded border-gray-300"
                />
              </div>
              <p className="text-xs text-gray-500">
                Used to deliver personalized advertisements.
              </p>
            </div>

            <div className="flex space-x-2 mt-4">
              <button
                onClick={handleAcceptAll}
                className="flex-1 bg-gray-800 hover:bg-gray-900 text-white text-xs font-medium py-2 px-4 rounded-md transition-colors"
              >
                Accept All
              </button>
              <button
                onClick={handleSaveSettings}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs font-medium py-2 px-4 rounded-md transition-colors"
              >
                Save Settings
              </button>
            </div>

            <button
              onClick={() => setShowSettings(false)}
              className="w-full text-gray-600 hover:text-gray-800 text-xs font-medium py-1 transition-colors"
            >
              Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
